import {useRouter} from 'next/router';
import {useCallback, useState} from 'react';
import {t} from '../../../../../common/getTranslations';
import {requestContext} from '../../../../../common/requestContext';
import useTypedSelector from '../../../../../common/useTypedSelector';
import {CommentItem} from '../../../../../design-system/organisms/CommentItem/CommentItem';
import {HeaderTitle} from '../../../../../design-system/organisms/HeaderTitle/HeaderTitle';
import {NoItem} from '../../../../../design-system/organisms/NoItem/NoItem';
import {PC_HEADER_HEIGHT} from '../../../../../design-system/organisms/PcHeader/constants';
import {CommentItem as CommentItemType} from '../../../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../../header/constants';
import {aggregateAndSendLog} from '../../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../../logging/putTiaraObjectProps';
import {showToast} from '../../../../../toast/toastHandler';
import {useInfiniteMyCommentListQuery} from '../../../../../wrapper';
import {EMPTY_COMMENT_TEXT} from '../../../constant';
import {CommentMoreMenuContainer} from '../../../container/CommentMoreMenuContainer';
import {useUpdateCommentEmoticon} from '../../../hook/useUpdateCommentEmoticon';
import {EmoticonScript} from '../../../presentational/EmoticonScript';
import {MyCommentListInfiniteFetcher} from '../headless/MyCommentListInfiniteFetcher';

export function MyCommentContainer() {
  const user = useTypedSelector(state => state.user.user);
  const commentList = useInfiniteMyCommentListQuery(
    {page: 0},
    {
      initialPageParam: {page: 0},
      getNextPageParam: (lastPage, pages) => {
        return lastPage.myCommentList?.isEnd ? undefined : {page: pages.length};
      },
    }
  ).data;
  const router = useRouter();

  const [removeTargetCommentItem, setRemoveTargetCommentItem] = useState<
    CommentItemType | undefined
  >();

  const onClickMore = useCallback((commentItem: CommentItemType) => {
    setRemoveTargetCommentItem(commentItem);
  }, []);

  const closeDialog = useCallback(() => {
    setRemoveTargetCommentItem(undefined);
  }, []);

  const onClickComment = useCallback(
    (commentItem: CommentItemType) => {
      router.push(
        `/comment/detail?commentUid=${
          // parentUid가 있는 경우는 내 댓글이 답글로 달린 경우이므로, 모댓글의 detail 페이지로 이동해야 합니다.
          commentItem.parentUid || commentItem.commentUid
        }&seriesId=${commentItem.seriesId}`
      );
    },
    [router]
  );

  const {isMobile} = requestContext.requestInfo.display;

  const onClickLike = useCallback(() => {
    showToast({
      message: t('comment_toast_like_impossible'),
      isMobile,
      delayMs: 2000,
    });
    return;
  }, [isMobile]);

  useUpdateCommentEmoticon({commentList});

  if (!user) {
    return null;
  }
  return (
    <div
      className="flex w-full flex-col items-center"
      style={{
        minHeight: `calc(${isMobile ? '100vh' : '852px'} - ${
          isMobile ? MOBILE_HEADER_HEIGHT_PX : PC_HEADER_HEIGHT
        }px)`,
      }}
    >
      <EmoticonScript />
      <div className="flex w-full flex-1 flex-col">
        {!isMobile && (
          <HeaderTitle className="mt-20pxr mb-32pxr px-15pxr">
            {t('mycomment_title')}
          </HeaderTitle>
        )}
        <MyCommentListInfiniteFetcher>
          {commentList?.pages.map(page =>
            page.myCommentList?.commentList.map(item => (
              <div
                key={item.commentUid}
                onClick={e =>
                  LOG_CLICK_EVENT('댓글본문_클릭', e, () =>
                    onClickComment(item)
                  )
                }
                className="cursor-pointer"
                {...putTiaraObjectProps({
                  eventMeta: {
                    id: String(item.commentUid),
                    type: 'commentId',
                    series_id: String(item.seriesId || ''),
                  },
                  customProps: {
                    ...(item.productId
                      ? {event_episode_id: String(item.productId)}
                      : {}),
                    event_comment_id: String(item.commentUid),
                  },
                })}
              >
                <CommentItem
                  item={item}
                  isReply={false}
                  isMyComment={true}
                  // /comment/my(댓글내역)에서만 권리침해 신고 문구가 아닌 댓글이 보이도록 합니다.
                  shouldShowTempComment={true}
                  onClickMore={e => {
                    e.stopPropagation();
                    LOG_CLICK_EVENT('삭제_클릭', e, () => {
                      onClickMore(item);
                    });
                  }}
                  onClickLike={e => {
                    e.stopPropagation();
                    LOG_CLICK_EVENT('좋아요_클릭', e, () => {
                      onClickLike();
                    });
                  }}
                  onClickReply={e => {
                    e.stopPropagation();
                    aggregateAndSendLog({
                      targetElem: e.target as HTMLElement,
                      eventMeta: {
                        id: String(item.commentUid),
                        type: 'commentId',
                        series_id: String(item.seriesId || ''),
                      },
                      actionType: 'Event',
                      actionName: '댓글달기_클릭',
                      customProps: {
                        user_action: 'click',
                      },
                    });
                    onClickComment(item);
                  }}
                />
              </div>
            ))
          )}
          {commentList &&
            commentList.pages[0].myCommentList?.commentList.length === 0 && (
              <NoItem isMobile={isMobile}>{EMPTY_COMMENT_TEXT}</NoItem>
            )}
        </MyCommentListInfiniteFetcher>
      </div>
      {removeTargetCommentItem && (
        <CommentMoreMenuContainer
          isMyComment={true}
          commentItem={removeTargetCommentItem}
          deactiveMoreMenu={closeDialog}
          isActiveMoreMenu={!!removeTargetCommentItem}
        />
      )}
    </div>
  );
}
